import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../Context/Can';
import { Box, Button, Chip, Dialog, DialogContent, IconButton, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { buttonStyles } from '../../../theme/formStyles';
import {
    dayNumber, engineerName, fmtDate, initials, isOnDay, isUnassigned, siteColor,
    subTaskStatusStyle, taskLength, todayYmd,
} from '../projectSchedule.utils';
import VisitPanel from './VisitPanel';
import SubTaskStatusControl from './SubTaskStatusControl';
import { DateRequestActions } from './DateRequests';

const sectionSx = { fontSize: '11.5px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.05em', mt: 2.25, mb: 0.75 };

// Details for one sub-task: status, site visits (check-in/out) and project info. Other changes are made in the existing project edit form.
export default function SubTaskDetailDialog({ task, currentUserId, onClose, onChanged, dateRequest, canAnswerRequests, onRequestAnswered, onEditProject }) {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const fullScreen = useMediaQuery('(max-width:700px)');
    if (!task) return null;

    const color = siteColor(task.facility_id);
    const len = taskLength(task);
    const today = todayYmd();
    const unassigned = isUnassigned(task);
    const isSpoc = !!task.spoc?.id && !!currentUserId && task.spoc.id.toLowerCase() === currentUserId.toLowerCase();
    // Mirrors the server rule: admins (who can edit projects) or the sub-task's own SPOC
    const canChangeStatus = isSpoc || ability.can('manage', 'EditProjectManagement');

    const openEditForm = () => {
        onClose();
        // The project editor when the host provides it, otherwise the Settings form
        if (onEditProject) onEditProject(task.project_id);
        else navigate(`/client/${task.client_id}/settings/project_management/edit/${task.project_id}`);
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs" fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.75, bgcolor: color, color: '#fff' }}>
                <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{task.facility_name}</Typography>
                <Chip size="small" label={task.status} sx={{ fontWeight: 700, bgcolor: '#fff', color: subTaskStatusStyle(task.status).color }} />
                <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: '#fff' }}><CloseIcon /></IconButton>
            </Box>
            <DialogContent sx={{ pt: 2 }}>
                <Typography sx={{ fontSize: '19px', fontWeight: 500, mb: 0.5 }}>{task.sub_task_name}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', color: '#6b7785', fontSize: '13.5px' }}>
                    <span>📅 {task.start_date === task.end_date ? fmtDate(task.start_date) : `${fmtDate(task.start_date)} – ${fmtDate(task.end_date)} · ${len} days`}</span>
                    {len > 1 && isOnDay(task, today) && <span>Day {dayNumber(task, today)} of {len}</span>}
                    {task.location_name && <span>📍 {task.location_name}</span>}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, border: '1px solid #e5e9ee', borderRadius: 3, p: 1.25, mt: 1.75 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 700, flex: 'none', bgcolor: unassigned ? '#fee2e2' : '#dbe7ff', color: unassigned ? '#dc2626' : '#1d64d8' }}>
                        {unassigned ? '!' : initials(engineerName(task))}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 500 }}>{engineerName(task)}</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>
                            {unassigned ? 'Assign a SPOC in the project edit form' : task.spoc?.id || 'Sub-task SPOC'}
                        </Typography>
                    </Box>
                </Box>

                {dateRequest && (
                    <Box sx={{ mt: 1.75, border: '1px solid #f5d9a8', bgcolor: '#fffaf0', borderRadius: 3, p: 1.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '13.5px', mb: 0.5 }}>📅 Client asked for another date</Typography>
                        <DateRequestActions request={dateRequest} canAnswer={canAnswerRequests} onDone={onRequestAnswered} />
                    </Box>
                )}

                <Typography sx={sectionSx}>Work status</Typography>
                <SubTaskStatusControl key={`status-${task.id}`} task={task} canChange={canChangeStatus} onChanged={onChanged} />

                <VisitPanel key={task.id} task={task} currentUserId={currentUserId} onChanged={onChanged} />

                <Typography sx={sectionSx}>Project</Typography>
                <Typography sx={{ fontSize: '13.5px' }}>{task.project_description || `Project #${task.project_id}`}</Typography>
                {task.task_name && <Typography sx={{ fontSize: '13px', color: '#6b7785' }}>Task: {task.task_name}</Typography>}

                {task.description && (
                    <>
                        <Typography sx={sectionSx}>Details</Typography>
                        <Typography sx={{ fontSize: '13.5px', whiteSpace: 'pre-wrap' }}>{task.description}</Typography>
                    </>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2.5 }}>
                    {ability.can('manage', 'EditProjectManagement') && (
                        <Button variant="contained" onClick={openEditForm} sx={buttonStyles.contained}>Edit project</Button>
                    )}
                    <Button variant="outlined" onClick={onClose} sx={buttonStyles.outlined}>Close</Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
